(() => {
  'use strict';

  const configNode = document.getElementById('nh-performance-config');
  if (!configNode) return;

  let config;
  try { config = JSON.parse(configNode.textContent || '{}'); } catch { return; }
  if (!config.enabled || config.designMode) return;

  const saveData = Boolean(navigator.connection?.saveData);
  const reduceForData = Boolean(config.dataSaver && saveData);
  if (reduceForData) document.documentElement.dataset.nhDataSaver = 'true';

  const componentRecords = new Map(
    (config.lazyComponents ? config.components || [] : []).map((entry) => [entry.src, { entry, state: 0, attempts: 0 }])
  );

  const elementRecord = new WeakMap();
  const componentObserver = componentRecords.size && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const item of entries) {
          if (!item.isIntersecting) continue;
          componentObserver.unobserve(item.target);
          loadComponent(elementRecord.get(item.target));
        }
      }, { rootMargin: '240px 0px' })
    : null;

  const observedVideos = new WeakSet();
  const videoObserver = config.pauseVideo && !reduceForData && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (video.dataset.nhWasAutoplay === 'true' && video.paused) video.play().catch(() => undefined);
          } else if (video.autoplay && !video.paused) {
            video.dataset.nhWasAutoplay = 'true';
            video.pause();
          }
        }
      }, { rootMargin: '240px 0px' })
    : null;

  const collect = (root, selector) => {
    const matches = root instanceof Element && root.matches(selector) ? [root] : [];
    return matches.concat(Array.from(root.querySelectorAll?.(selector) || []));
  };

  function loadComponent(record, retry = false) {
    if (!record || record.state === 1 || record.state === 2) return;
    if (record.state === 3 && (!retry || record.attempts >= 3)) return;

    // Native modules may have been imported explicitly by a dynamic Horizon
    // lifecycle (for example recommendations or quick add). Do not inject a
    // second script element when any custom-element selector is already defined.
    const alreadyDefined = record.entry.selectors.some((selector) =>
      /^[a-z][a-z0-9._-]*-[a-z0-9._-]+$/.test(selector) && customElements.get(selector)
    );
    if (alreadyDefined) {
      record.state = 2;
      return;
    }

    record.state = 1;
    record.attempts += 1;
    const script = document.createElement('script');
    const source = new URL(record.entry.src, location.href);
    // A failed module URL may remain in the module map for this document.
    // Fresh intent can retry twice, without changing successful canonical imports.
    if (record.attempts > 1) source.searchParams.set('nh_retry', String(record.attempts - 1));
    script.src = source.href;
    if (record.entry.module !== false) script.type = 'module';
    script.dataset.nhLazyComponent = '';
    script.addEventListener('load', () => { record.state = 2; }, { once: true });
    script.addEventListener('error', () => {
      record.state = 3;
      script.remove();
    }, { once: true });
    document.head.append(script);
  }

  function isCriticalPlacement(element, record) {
    if (!(element instanceof Element)) return false;
    if (record?.entry?.eager === true) {
      const media = record.entry.eagerMedia;
      if (!media || window.matchMedia(media).matches) return true;
    }
    if (element.matches('[data-critical], [data-critical-media]') || element.closest('[data-critical], [data-critical-media]')) return true;
    return false;
  }

  function componentMatches(root, record) {
    const selector = record.entry.selectors.join(',');
    if (!selector) return [];
    try { return collect(root, selector); } catch { return []; }
  }

  function scanComponents(root, force = false) {
    componentRecords.forEach((record) => {
      if (record.state) return;
      const matches = componentMatches(root, record);
      if (!matches.length) return;

      if (force || !componentObserver || matches.some((element) => isCriticalPlacement(element, record))) {
        loadComponent(record);
        return;
      }

      for (const element of matches) {
        if (!(element instanceof Element)) continue;
        elementRecord.set(element, record);
        componentObserver.observe(element);
      }
    });
  }

  function scanMedia(root) {
    if (config.lazyMedia) {
      collect(root, 'iframe:not([loading])').forEach((frame) => {
        frame.loading = frame.closest('[data-critical], [data-critical-media]') ? 'eager' : 'lazy';
      });

      if (reduceForData) {
        collect(root, 'video').forEach((video) => {
          if (video.closest('main > .shopify-section:first-child, [data-critical-media]')) return;
          video.preload = 'none';
          if (video.autoplay) video.dataset.nhAutoplay = 'true';
          video.autoplay = false;
          video.pause?.();
        });
      }
    }

    if (videoObserver) {
      collect(root, 'video[autoplay]').forEach((video) => {
        if (observedVideos.has(video)) return;
        observedVideos.add(video);
        videoObserver.observe(video);
      });
    }
  }

  function setupSectionLifecycle() {
    // Keep Horizon/Shopify section lifecycle rescans without applying size containment
    // to entire sections. Full-section content-visibility used a guessed intrinsic
    // height and could change the document geometry as a section approached the
    // viewport, which is especially noticeable around dynamically hydrated content.
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target;
      if (!(section instanceof Element)) return;
      scanComponents(section, true);
      scanMedia(section);
    });
  }

  function setupIntentPrefetch() {
    const connection = navigator.connection;
    if (!config.intentPrefetch || saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    const fetched = new Set();
    const pageBudget = 6;
    const concurrencyLimit = 2;
    let inFlight = 0;
    let timer = 0;
    let pendingUrl = '';
    const eligible = (anchor) => {
      if (!anchor || anchor.target || anchor.hasAttribute('download') || anchor.closest('[data-nh-no-prefetch]')) return null;
      let url;
      try { url = new URL(anchor.href, location.href); } catch { return null; }
      if (url.origin !== location.origin || url.href === location.href || url.hash) return null;
      if (!['http:', 'https:'].includes(url.protocol)) return null;
      // Storefront content only, with an optional Shopify locale prefix.
      const path = url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, '');
      if (!/^\/(?:products\/[^/]+|collections(?:\/[^/]+)?(?:\/products\/[^/]+)?|pages\/[^/]+|blogs\/[^/]+(?:\/[^/]+)?)\/?$/.test(path)) return null;
      if ([...url.searchParams.keys()].some((key) => !['variant', 'page', 'sort_by'].includes(key) && !key.startsWith('filter.'))) return null;
      return url.href;
    };
    const cancel = () => { clearTimeout(timer); timer = 0; pendingUrl = ''; };
    const schedule = (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      const url = eligible(anchor);
      cancel();
      if (!url || fetched.has(url) || fetched.size >= pageBudget || inFlight >= concurrencyLimit) return;
      pendingUrl = url;
      timer = window.setTimeout(() => {
        if (!pendingUrl || fetched.has(pendingUrl) || fetched.size >= pageBudget || inFlight >= concurrencyLimit) return;
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = pendingUrl;
        link.as = 'document';
        fetched.add(pendingUrl);
        inFlight += 1;
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          inFlight -= 1;
        };
        link.addEventListener('load', settle, { once: true });
        link.addEventListener('error', settle, { once: true });
        document.head.append(link);
        pendingUrl = '';
      }, Number(config.prefetchDelay) || 150);
    };
    document.addEventListener('pointerover', schedule, { passive: true });
    document.addEventListener('pointerout', cancel, { passive: true });
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', cancel);
  }

  function setupInteractionComponentLoading() {
    if (!componentRecords.size) return;

    const forceNearest = (event) => {
      if (!(event.target instanceof Element)) return;
      componentRecords.forEach((record) => {
        if (record.state === 1 || record.state === 2) return;
        for (const selector of record.entry.selectors) {
          try {
            if (event.target.closest(selector)) {
              loadComponent(record, true);
              break;
            }
          } catch { /* invalid selector from config */ }
        }
      });
    };

    // pointerdown/focusin happen before click/keyboard activation, giving undefined
    // custom elements a chance to upgrade without a document-wide mutation watcher.
    document.addEventListener('pointerdown', forceNearest, { capture: true, passive: true });
    document.addEventListener('focusin', forceNearest, { capture: true });

    window.addEventListener('online', () => {
      componentRecords.forEach((record) => {
        if (record.state === 3 && componentMatches(document, record).length) loadComponent(record, true);
      });
    });

    document.addEventListener('cart:rendered', (event) => {
      if (!(event.target instanceof Element)) return;
      scanComponents(event.target, true);
      scanMedia(event.target);
    });

    document.addEventListener('theme-drawer:open', (event) => {
      const drawer = event.target instanceof Element ? event.target.closest('theme-drawer') : null;
      if (drawer) {
        scanComponents(drawer, true);
        scanMedia(drawer);
      }
    });

    document.addEventListener('dialog:open', (event) => {
      const dialogComponent = event.target instanceof Element ? event.target.closest('dialog-component') : null;
      if (dialogComponent) {
        scanComponents(dialogComponent, true);
        scanMedia(dialogComponent);
      }
    });
  }

  scanComponents(document);
  scanMedia(document);
  setupSectionLifecycle();
  setupIntentPrefetch();
  setupInteractionComponentLoading();

  // Deliberately no document-wide MutationObserver here. Horizon's own components
  // already expose lifecycle boundaries (sections, drawers, lazy recommendations),
  // and scanning every third-party/app DOM insertion multiplied main-thread work.
})();
